import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import { Logo } from '../components/Logo';
import { useUserContext } from '../context/UserContext';
import { userActionTypes, cartActionTypes, cartItemActionTypes, userTypes, AuthRoutes } from '../app/enums';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import Backdrop from '@material-ui/core/Backdrop';
import { useCartContext } from '../context/CartContext';
import { Authentication, Carts, Searches } from '../api/api';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { isEmpty } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: '#ffffff',
    },
    container: {
      padding: 25,
    },
    paper: { 
      padding: 25,
      marginTop: theme.spacing(8),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    form: {
      width: '100%', 
      marginTop: theme.spacing(1),
    },
    submit: {
      margin: theme.spacing(3, 0, 2),
    },
    logo: { 
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  }),
);

export const Login = () => {
  const history = useHistory();
  const classes = useStyles();
  const { state, dispatch } = useUserContext();
  const { userName, password } = state.self;
  const [ loginError, setLoginError ] = useState('');
  const [ loading, setLoading ] = useState(true);
  const cart = useCartContext();

  const lsLocation = localStorage.getItem('locations')
  const [ lsLoggedInUser, setLsLoggedInUser ] = useState(localStorage.getItem('user'));
  const [ lsRefreshToken, setLsRefreshToken ] = useState(localStorage.getItem('refreshToken'));

  //Get new accessToken
  useEffect(() => { 
    if(lsLoggedInUser && lsRefreshToken){
      setLoading(true);

      if(isEmpty(state.accessToken)){        
        Authentication.getAccessToken(lsRefreshToken)
          .then((data) =>{
            const { refreshToken, accessToken } = data

            localStorage.setItem('refreshToken', refreshToken);
            const foundUser = JSON.parse(lsLoggedInUser);

            dispatch({ 
              type: userActionTypes.LOAD_USER, 
              user: foundUser, 
              accessToken: accessToken
            });

            if(foundUser.userType === userTypes.EXTERNAL && lsLocation){
              const locations = JSON.parse(lsLocation)
              loadSelfCart(locations[0].id)
            }

            if(isEmpty(state.currentPage)){
              history.push(foundUser.userType === userTypes.EXTERNAL ? AuthRoutes.search : AuthRoutes.adminSearch);
            }else{
              history.push(state.currentPage || '/');
            }
          })
          .catch((e) => {
            localStorage.removeItem('user');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('locations');
            setLsRefreshToken(null)
            setLsLoggedInUser(null)
          });
      }else{
        if(!isEmpty(state.currentPage)){
          history.push(state.currentPage || '/');
        }
      }
    }else{
      setLoading(false);
    }

  },[dispatch, history, lsLocation, lsLoggedInUser, lsRefreshToken, state.accessToken, state.currentPage])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string) => {
    dispatch({
      type: 'field',
      fieldName: fieldName,
      payload: event.currentTarget.value,
    })
    setLoginError('')
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    let locationId: string = '';
    
    Authentication.login(userName, password || '')
      .then((data) => {
        const { refreshToken, user, locations } = data

        if(locations.length > 0){
          locationId = locations[0].id 
          localStorage.setItem('locations', JSON.stringify(locations))
        }
          
        dispatch({ type: userActionTypes.LOGIN_USER, user: data})
        localStorage.setItem('user', JSON.stringify(user))
        localStorage.setItem('refreshToken', refreshToken);

        if(locationId.length > 0) {
          loadSelfCart(locationId)
          history.push(AuthRoutes.search);
        } else {
          history.push(AuthRoutes.adminSearch);
        }       
      }).catch((error) => {
        setLoginError('Invalid credentials')
      }).finally(() => {
        setLoading(false);
      });      
  };

  const loadSelfCart = (locationId: string) => {
    Carts.getSelfCarts(locationId)
      .then((data) => {
        cart.dispatch({ type: cartActionTypes.LOAD_CART, cart: data[0] })        
      })
      .then(() => {
        if(cart.state.cartItems.length > 0){
          cart.state.cartItems.forEach((item) => {
            Searches.getSearch(item.searchId)
              .then((search) => {
                cart.dispatch({ 
                  type: cartActionTypes.UPDATE_CART_ITEM, 
                  item: item, 
                  action: cartItemActionTypes.UPDATE_CART_ITEM_VEHICLE,
                  search: search 
                })
              })
          })
        }
    })
    .catch((error) => {
      console.log('Cart Drawer error', error)
    }) 
  }

  return (
    <Container component="main" maxWidth="xs" className={classes.container}> 
      {loading ? 
        <Backdrop className={classes.backdrop} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      : 
        <Paper className={classes.paper} elevation={3}>
            <div className={classes.logo}>
              <Logo />
            </div>
            
            <form className={classes.form} onSubmit={handleSubmit}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="user"
                label="User"
                name="user"
                autoComplete="current-user"
                error = {loginError !== ''}
                helperText = {loginError || '' }
                onChange={(e) => handleChange(e, 'userName')}
                autoFocus
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                error = {loginError !== ''}
                helperText = {loginError || '' }
                onChange={(e) => handleChange(e, 'password')}
                autoComplete="current-password"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
              >
                LOGIN
              </Button>
            </form>
          <Box mt={8}>    
          </Box>
        </Paper>
      }       
    </Container>
  
  );
};

export default Login;
